import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { submitNote } from "../../../enums/queryFunction/Submit";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PETS_NOTES } from "../../../enums/QueryKeys";
import { EditNoteOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  FormHelperText,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

export default function AddPetNoteForm() {
  const { id: petId } = useParams();
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const queryClient = useQueryClient();
  const [charCount, setCharCount] = useState(0);
  const maxCharacters = 300;
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      description: "",
    },
  });

  const hasError = !!errors.description;

  const onSubmit = async (data) => {
    await submitNote({ description: data.description }, petId);
    queryClient.invalidateQueries({ queryKey: [PETS_NOTES + petId] });

    reset();
    handleClose(false);
  };

  return (
    <Box>
      <Button
        type="submit"
        color="primary"
        variant="contained"
        startIcon={<EditNoteOutlined />}
        onClick={handleClickOpen}
        sx={{ mb: 3 }}
        data-testid="pet.notes.add-button"
      >
        {t("AddNote")}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ "data-testid": "pet.notes.dialog" }}
      >
        <DialogTitle>{t("PostNote")}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={0}>
              <Controller
                name="description"
                control={control}
                rules={{
                  required: t("CantBeEmpty"),
                  maxLength: { value: 300, message: t("IllegalLength") + 300 },
                  validate: {
                    trim: (value) => value.trim() !== "" || t("CantBeEmpty"),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    sx={{ mt: 1 }}
                    label={t("Note")}
                    multiline
                    rows={4}
                    fullWidth
                    error={!!errors.description}
                    inputProps={{
                      maxLength: 300,
                      "data-testid": "pet.notes.description-input",
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      setCharCount(e.target.value.length);
                    }}
                  />
                )}
              />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 0 }}
              >
                <FormHelperText
                  error={hasError}
                  sx={{ mr: 2 }}
                  data-testid="pet.notes.error-message"
                >
                  {hasError && errors.description.message}
                </FormHelperText>

                <FormHelperText  data-testid="pet.notes.char-count">
                  <Typography
                    variant="caption"
                    color={
                      charCount > maxCharacters ? "error" : "textSecondary"
                    }
                  >
                    {charCount}/{maxCharacters}
                  </Typography>
                </FormHelperText>
              </Box>

              <DialogActions>
                <Tooltip title={t("PostNote")}>
                  <Button
                    type="submit"
                    variant="contained"
                    data-testid="pet.notes.submit-button"
                  >
                    {t("Add")}
                  </Button>
                </Tooltip>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  color="error"
                  data-testid="pet.notes.cancel-button"
                >
                  {t("Cancel")}
                </Button>
              </DialogActions>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
